import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { parameterListStore } from '../components/ParameterListBrowser';
import { useTranslation } from 'react-i18next';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useMutation, useSubscription } from 'hooks/apollo';
import { ServiceContext } from 'providers/ServiceProvider';
import { useToast } from 'hooks/toasts/useToast';
import { RUN_HEALTH_CHECKS } from '../mutations';
import { HEALTH_CHECKS_COMPLETED } from '../subscriptions';
import variables from '../variables';
import HealthChecksActionIcon from '../components/HealthChecksActionIcon';

const defaultDisabled = {
  healthChecks: false
};

const useHealthChecksAction = () => {
  const [disabled, setDisabled] = useState(defaultDisabled);
  const { t } = useTranslation();
  const { addActions, store } = useListBrowserUtils(parameterListStore);
  const confirm = useConfirmDialog();
  const [activeService] = useContext(ServiceContext);
  const addToast = useToast();

  useEffect(() => {
    variables.parametersHealthChecks([]);
  }, [activeService]);

  useSubscription(HEALTH_CHECKS_COMPLETED, {
    variables: { serviceName: activeService?.name ?? '-' }, // used to trigger the subscription when the service changes
    onData: ({ data }) => {
      variables.parametersHealthChecks(data.data.healthChecksCompleted.parameters);
      setDisabled({ healthChecks: false });
      addToast(t('BehaviorParamTypes.HealthChecksCompleted'), 'info');
    }
  });

  const [runHealthChecks] = useMutation(RUN_HEALTH_CHECKS);

  const handleHealthCheckOperation = useCallback(() => {
    confirm(
      () => {
        if (!store.values?.length) {
          addToast(t('BehaviorParamTypes.NoParametersAvailable'), 'warning');
          return;
        }

        setDisabled({ healthChecks: true });
        const parameters = store.allValues.map(param => ({
          parameterCode: param.paramCode,
          isInitializationParam: param.isInitializationParam ?? false
        }));
        runHealthChecks({ variables: { input: { parameters } } });
      },
      {
        title: t('BehaviorParamTypes.RunHealthChecks')
      }
    );
  }, [confirm, t, store.values, store.allValues, runHealthChecks, addToast]);

  useEffect(() => {
    addActions([
      {
        name: 'run-health-check',
        label: t('BehaviorParamTypes.RunHealthChecks'),
        icon: <HealthChecksActionIcon />,
        handler: handleHealthCheckOperation,
        disabled: disabled.healthChecks
      }
    ]);
  }, [addActions, t, disabled.healthChecks, handleHealthCheckOperation]);
};

export default useHealthChecksAction;
